import React, { useState, useEffect } from 'react'
import { Mail, Send, Inbox, Paperclip, Calendar, ChevronDown, ChevronUp, Download } from 'lucide-react'
import http from '../../config/http'
import { formatDateToDMY } from '../../utils/formatDate'

export default function EmailViewer({ customerId }) {
  const [emails, setEmails] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedEmailId, setExpandedEmailId] = useState(null)
  const [filter, setFilter] = useState('all') // 'all', 'sent', 'received'

  useEffect(() => {
    if (customerId) {
      fetchEmails()
    }
  }, [customerId])

  const fetchEmails = async () => {
    try {
      setLoading(true)
      const { data } = await http.get(`/api/emails?customerId=${customerId}`)
      console.log('Fetched emails:', data)
      setEmails(data)
    } catch (error) {
      console.error('Error fetching emails:', error)
      setEmails([])
    } finally {
      setLoading(false)
    }
  }

  const toggleEmailExpand = (emailId) => {
    setExpandedEmailId(expandedEmailId === emailId ? null : emailId)
  }

  const getFilteredEmails = () => {
    if (filter === 'all') return emails
    return emails.filter(email => email.sense === filter)
  }

  const formatDate = (date) => {
    if (!date) return ''
    const emailDate = new Date(date)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (emailDate.toDateString() === today.toDateString()) {
      return `Today at ${emailDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
    } else if (emailDate.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${emailDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
    } else {
      return formatDateToDMY(date)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  const filteredEmails = getFilteredEmails()

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header with Filters */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Mail className="h-5 w-5 text-purple-600" />
            Email History
          </h3>
          <span className="text-sm text-gray-500">
            {filteredEmails.length} {filteredEmails.length === 1 ? 'email' : 'emails'}
          </span>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              filter === 'all'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All ({emails.length})
          </button>
          <button
            onClick={() => setFilter('sent')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
              filter === 'sent'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Send className="h-3 w-3" />
            Sent ({emails.filter(e => e.sense === 'sent').length})
          </button>
          <button
            onClick={() => setFilter('received')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
              filter === 'received'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Inbox className="h-3 w-3" />
            Received ({emails.filter(e => e.sense === 'received').length})
          </button>
        </div>
      </div>

      {/* Email List */}
      <div className="divide-y divide-gray-200">
        {filteredEmails.length === 0 ? (
          <div className="p-8 text-center">
            <Mail className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No emails found</p>
            <p className="text-gray-400 text-xs mt-1">
              {filter !== 'all' 
                ? `No ${filter} emails for this customer`
                : 'Email communication will appear here'}
            </p>
          </div>
        ) : (
          filteredEmails.map((email) => (
            <div key={email._id} className="hover:bg-gray-50 transition-colors">
              {/* Email Header - Clickable */}
              <div
                onClick={() => toggleEmailExpand(email._id)}
                className="p-4 cursor-pointer"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      email.sense === 'sent' 
                        ? 'bg-blue-100 text-blue-600' 
                        : 'bg-green-100 text-green-600'
                    }`}>
                      {email.sense === 'sent' ? (
                        <Send className="h-4 w-4" />
                      ) : (
                        <Inbox className="h-4 w-4" />
                      )}
                    </div>

                    {/* Email Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-gray-900 truncate">
                          {email.subject || '(No Subject)'}
                        </h4>
                        {email.attachments && email.attachments.length > 0 && (
                          <Paperclip className="h-3 w-3 text-gray-400 flex-shrink-0" />
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                        <span className="font-medium">
                          {email.sense === 'sent' ? 'To:' : 'From:'}
                        </span>
                        <span className="truncate">
                          {email.sense === 'sent' ? email.to : email.from}
                        </span>
                      </div>

                      {expandedEmailId !== email._id && (
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {email.body?.replace(/<[^>]*>/g, '') || ''}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Date and Expand Icon */}
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Calendar className="h-3 w-3" />
                      <span className="whitespace-nowrap">{formatDate(email.date)}</span>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      email.status === 'Sent' 
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {email.status}
                    </span>
                    {expandedEmailId === email._id ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Email Body */}
              {expandedEmailId === email._id && (
                <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                  <div className="mt-3 space-y-3">
                    {/* Full Email Details */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="font-medium text-gray-500">From:</span>
                        <p className="text-gray-900">{email.from}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-500">To:</span>
                        <p className="text-gray-900">{email.to}</p>
                      </div>
                    </div>

                    {/* Email Body */}
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div 
                        className="text-sm text-gray-800 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: email.body }}
                      />
                    </div>

                    {/* Attachments */}
                    {email.attachments && email.attachments.length > 0 && (
                      <div className="space-y-2">
                        <h5 className="text-xs font-medium text-gray-700 flex items-center gap-1">
                          <Paperclip className="h-3 w-3" />
                          Attachments ({email.attachments.length})
                        </h5>
                        <div className="space-y-1">
                          {email.attachments.map((attachment, index) => (
                            <a
                              key={index}
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors text-xs"
                            >
                              <Paperclip className="h-3 w-3 text-gray-400" />
                              <span className="flex-1 truncate text-gray-700">
                                {attachment.filename}
                              </span>
                              <Download className="h-3 w-3 text-gray-400" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
